/**
 * SmartImprove ROI tab — Feb 2026 redesign.
 *
 * Executive business-case dashboard mirroring SmartEvaluate's ROI layout:
 *   - KPI strip (Total Investment, Annual Savings, Payback, 3-Year ROI)
 *   - Top Value Drivers (% of annual savings, bar list)
 *   - Investment Breakdown (CAPEX + OPEX) sourced from Implementation v2
 *   - Benefit Realization Timeline (Year 1 ramp → Year 2/3 steady-state)
 *   - Model Inputs (Advanced) collapsed section exposing the shared
 *     `roi_assumptions` snapshot
 *   - Edit ROI Inputs / Override dialog (annual savings pin, Year-1
 *     realization %, confidence multiplier %)
 *
 * Inputs auto-flow from accepted recommendations + non-archived
 * Implementation v2 plans. Consultant overrides persist in
 * `smartimprove_roi_overrides`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DollarSign,
  TrendingUp,
  Clock,
  Award,
  Pencil,
  RotateCcw,
  Info,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { OverlapWarning } from "../OverlapWarning";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({
  baseURL: `${BACKEND_URL}/api/smartimprove`,
  withCredentials: true,
});

const fmtMoney = (n, currency = "USD") => {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency, currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0,
    }).format(v);
  } catch (e) {
    return `${currency} ${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
};

const fmtMonths = (n) => (n == null ? "—" : `${Number(n).toFixed(1)} mo`);
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);

export default function RoiTab({ engagementId, isFinalized = false }) {
  const [dashboard, setDashboard] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [d, o] = await Promise.all([
        ax.get(`/engagements/${engagementId}/roi-dashboard`),
        ax.get(`/engagements/${engagementId}/roi-overrides`),
      ]);
      setDashboard(d.data || null);
      setOverrides(o.data || {});
    } catch {
      toast.error("Failed to load ROI dashboard");
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Live currency sync: refetch when the Implementation tab changes currency.
  useEffect(() => {
    const onCur = (e) => {
      if (!e?.detail?.engagementId || e.detail.engagementId === engagementId) reload();
    };
    window.addEventListener("smartimprove:currency-changed", onCur);
    return () => window.removeEventListener("smartimprove:currency-changed", onCur);
  }, [engagementId, reload]);

  if (loading) {
    return <div className="text-sm text-[#6B7280]">Loading ROI…</div>;
  }
  if (!dashboard) return null;

  if (dashboard.is_empty) {
    return (
      <EmptyState
        title="No ROI yet"
        description={
          dashboard.empty_reason ||
          "Accept recommendations and finalize implementation plans to generate ROI."
        }
      />
    );
  }

  return (
    <div className="space-y-5 max-w-7xl mx-auto" data-testid="smartimprove-roi-tab">
      <Header
        dashboard={dashboard}
        isFinalized={isFinalized}
        onEdit={() => setEditing(true)}
      />

      <OverlapWarning
        engagementId={engagementId}
        scope="roi"
        description="Some quantified recommendations address the same findings. Review recommendation selection to avoid double-counting benefits. Annual Savings is not adjusted automatically."
      />

      <KpiStrip dashboard={dashboard} />

      <TopValueDrivers dashboard={dashboard} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <InvestmentBreakdown dashboard={dashboard} />
        <BenefitTimeline dashboard={dashboard} />
      </div>

      <RecommendationRoiInputs
        engagementId={engagementId}
        currency={dashboard.currency || "USD"}
      />

      {editing ? (
        <OverrideDialog
          engagementId={engagementId}
          overrides={overrides}
          dashboard={dashboard}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await reload();
          }}
          onReset={async () => {
            try {
              await ax.put(`/engagements/${engagementId}/roi-overrides`, { reset: true });
              toast.success("Overrides cleared");
              setEditing(false);
              await reload();
            } catch {
              toast.error("Failed to reset overrides");
            }
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
function Header({ dashboard, isFinalized, onEdit }) {
  return (
    <header className="flex items-start justify-between flex-wrap gap-3">
      <div>
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
          Executive Business Case
        </p>
        <h2
          className="text-xl font-bold text-[#111827] mt-0.5"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Return on Investment
        </h2>
        <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
          Built from {dashboard.recommendation_count} accepted recommendation
          {dashboard.recommendation_count === 1 ? "" : "s"} (
          {dashboard.strategic_count} strategic) and {dashboard.investment.plans_count}{" "}
          implementation plan{dashboard.investment.plans_count === 1 ? "" : "s"}.
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {dashboard.override_active ? (
          <span
            data-testid="roi-override-badge"
            className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]"
          >
            Consultant Override Active
          </span>
        ) : null}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// KPI Strip
// ---------------------------------------------------------------------------
function KpiStrip({ dashboard }) {
  const investment = dashboard.investment.one_time;
  const annual = dashboard.annual_savings_usd;
  const payback = dashboard.overall_payback_months;
  const roi = dashboard.overall_roi_percent;
  const currency = dashboard.currency || "USD";

  return (
    <section
      data-testid="roi-kpis"
      className="grid grid-cols-2 md:grid-cols-4 gap-3"
    >
      <KpiTile
        icon={<DollarSign className="w-3.5 h-3.5" />}
        label="Total Investment (one-time)"
        value={fmtMoney(investment, currency)}
        secondary={
          dashboard.annual_recurring_usd > 0
            ? `+ ${fmtMoney(dashboard.annual_recurring_usd, currency)} / yr recurring`
            : null
        }
        testId="roi-kpi-investment"
      />
      <KpiTile
        icon={<TrendingUp className="w-3.5 h-3.5" />}
        label="Annual Savings"
        value={fmtMoney(annual, currency)}
        secondary={`Year 1: ${fmtMoney(dashboard.savings_by_year.year1, currency)}`}
        testId="roi-kpi-savings"
      />
      <KpiTile
        icon={<Clock className="w-3.5 h-3.5" />}
        label="Payback Period"
        value={fmtMonths(payback)}
        secondary="From go-live"
        testId="roi-kpi-payback"
      />
      <KpiTile
        icon={<Award className="w-3.5 h-3.5" />}
        label="3-Year ROI"
        value={fmtPct(roi)}
        secondary={`Net benefit: ${fmtMoney(dashboard.savings_by_year.three_year_net, currency)}`}
        hero
        testId="roi-kpi-roi"
      />
    </section>
  );
}

function KpiTile({ icon, label, value, secondary, testId, hero = false }) {
  if (hero) {
    return (
      <div
        data-testid={testId}
        className="bg-[#025979] text-white rounded-sm px-3 py-2.5 shadow-sm"
      >
        <p className="text-[10px] uppercase tracking-wider text-white/70 flex items-center gap-1">
          {icon}
          {label}
        </p>
        <p
          className="text-lg font-extrabold mt-0.5"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {value}
        </p>
        {secondary ? <p className="text-[10px] text-white/70 mt-0.5">{secondary}</p> : null}
      </div>
    );
  }
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[#E5E7EB] rounded-sm px-3 py-2.5"
    >
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] flex items-center gap-1">
        {icon}
        {label}
      </p>
      <p className="text-base font-bold text-[#0F172A] mt-1 tabular-nums">{value}</p>
      {secondary ? <p className="text-[10px] text-[#6B7280] mt-0.5">{secondary}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top Value Drivers
// ---------------------------------------------------------------------------
function TopValueDrivers({ dashboard }) {
  const total = Number(dashboard.annual_savings_usd) || 0;
  const drivers = (dashboard.value_drivers || []).filter((d) => d.amount > 0);
  if (drivers.length === 0 || total <= 0) return null;
  const maxAmt = Math.max(...drivers.map((d) => d.amount));
  const currency = dashboard.currency || "USD";
  return (
    <section
      data-testid="roi-value-drivers"
      className="bg-white border border-[#E5E7EB] rounded-sm"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2 px-5 pt-4 pb-2">
        <h3
          className="text-[11px] uppercase tracking-wider text-[#025979] font-semibold"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Benefit Contribution by Recommendation
        </h3>
        <p className="text-[10.5px] text-[#6B7280]">
          Share of quantified annual savings
        </p>
      </header>
      <ol>
        {drivers.map((d, i) => {
          const pct = total > 0 ? Math.round((d.amount / total) * 100) : 0;
          const barPct = maxAmt > 0 ? Math.round((d.amount / maxAmt) * 100) : 0;
          return (
            <li
              key={d.key}
              data-testid={`roi-driver-${d.key}`}
              className="flex items-center gap-4 px-5 py-2.5 border-t border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors"
            >
              <span className="w-5 text-[12px] font-semibold text-[#025979] tabular-nums">
                {i + 1}.
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] text-[#0F172A] font-medium truncate">
                  {d.label}
                </p>
                <div className="h-1.5 mt-1 bg-[#F3F4F6] rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-emerald-500"
                    style={{ width: `${barPct}%` }}
                  />
                </div>
              </div>
              <span className="w-12 text-right text-[12.5px] font-semibold text-[#065F46] tabular-nums">
                {pct}%
              </span>
              <span className="w-36 text-right text-[12.5px] font-bold text-[#0F172A] tabular-nums">
                {fmtMoney(d.amount, currency)}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Investment Breakdown
// ---------------------------------------------------------------------------
function InvestmentBreakdown({ dashboard }) {
  const currency = dashboard.currency || "USD";
  const oneTime = dashboard.investment.one_time;
  const recurring = dashboard.investment.recurring;

  return (
    <section
      data-testid="roi-investment-breakdown"
      className="bg-white border border-[#E5E7EB] rounded-sm p-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-2">
        Investment Breakdown
      </p>
      <div className="grid grid-cols-1 gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0F172A] mb-1">
            One-Time Investment (CAPEX)
          </p>
          <Row label="Implementation Plans" value={fmtMoney(oneTime, currency)} testId="roi-capex-impl" />
          <Row
            label={`Effort (${Math.round(dashboard.investment.hours).toLocaleString()} hrs · ${Math.round(dashboard.investment.weeks)} wks)`}
            value="included"
            muted
            testId="roi-capex-effort"
          />
          <div className="mt-1 pt-1.5 border-t border-[#E5E7EB] flex items-baseline justify-between py-1">
            <span className="text-[11px] font-semibold text-[#111827]">
              Total One-Time
            </span>
            <span
              className="text-[12px] font-bold text-[#025979] tabular-nums"
              data-testid="roi-capex-total"
            >
              {fmtMoney(oneTime, currency)}
            </span>
          </div>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0F172A] mb-1">
            Annual Recurring (OPEX)
          </p>
          <Row
            label="Support / AMS / License"
            value={fmtMoney(recurring, currency)}
            testId="roi-opex-ams"
          />
          <div className="mt-1 pt-1.5 border-t border-[#E5E7EB] flex items-baseline justify-between py-1">
            <span className="text-[11px] font-semibold text-[#111827]">
              Total Annual Recurring
            </span>
            <span
              className="text-[12px] font-bold text-[#025979] tabular-nums"
              data-testid="roi-opex-total"
            >
              {fmtMoney(recurring, currency)}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value, muted = false, testId }) {
  return (
    <div className="flex items-baseline justify-between py-1" data-testid={testId}>
      <span className={`text-[11px] ${muted ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}>
        {label}
      </span>
      <span
        className={`text-[12px] ${
          muted ? "text-[#9CA3AF]" : "font-semibold text-[#0F172A]"
        } tabular-nums`}
      >
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Benefit Realization Timeline
// ---------------------------------------------------------------------------
function BenefitTimeline({ dashboard }) {
  const sby = dashboard.savings_by_year || {};
  const currency = dashboard.currency || "USD";
  const cells = [
    { label: "Year 1", value: sby.year1, note: "Ramp-up benefit" },
    { label: "Year 2", value: sby.year2, note: "Steady-state" },
    { label: "Year 3", value: sby.year3, note: "Steady-state" },
  ];
  const max = Math.max(...cells.map((c) => Number(c.value) || 0));
  return (
    <section
      data-testid="roi-benefit-timeline"
      className="bg-white border border-[#E5E7EB] rounded-sm p-4"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979]">
          Benefit Realization Timeline
        </p>
        <p className="text-[10.5px] text-[#6B7280]">
          3-yr total:{" "}
          <strong className="text-[#0F172A]">
            {fmtMoney(sby.three_year_total, currency)}
          </strong>
        </p>
      </div>
      <div className="space-y-2">
        {cells.map((c) => {
          const barPct = max > 0 ? Math.round(((Number(c.value) || 0) / max) * 100) : 0;
          return (
            <div key={c.label} className="flex items-center gap-3">
              <span className="w-14 text-[11px] font-semibold text-[#025979]">
                {c.label}
              </span>
              <div className="flex-1">
                <div className="h-2 bg-[#F3F4F6] rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-[#0EA5E9]"
                    style={{ width: `${barPct}%` }}
                  />
                </div>
                <p className="text-[10px] text-[#6B7280] mt-0.5">{c.note}</p>
              </div>
              <span
                className="w-32 text-right text-[12px] font-bold text-[#0F172A] tabular-nums"
                data-testid={`roi-year-${c.label.toLowerCase().replace(" ", "")}`}
              >
                {fmtMoney(c.value, currency)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recommendation ROI Inputs (Step 1) — bottom-up, per Primary Recommendation.
// Coexists ABOVE the legacy Model Inputs; does not yet feed the upper KPIs.
// ---------------------------------------------------------------------------
const ROI_STATUS = {
  calculated: { label: "Calculated", cls: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]" },
  override: { label: "Consultant Override", cls: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]" },
  partial: { label: "Partially Calculated", cls: "bg-[#FEFCE8] text-[#A16207] border-[#FDE68A]" },
  baseline_required: { label: "Baseline Required", cls: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]" },
  unmapped: { label: "Mapping Required", cls: "bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]" },
};

function RecommendationRoiInputs({ engagementId, currency }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const res = await ax.get(`/engagements/${engagementId}/roi-recommendation-inputs`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load recommendation ROI inputs");
    }
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onCur = (e) => {
      if (!e?.detail?.engagementId || e.detail.engagementId === engagementId) load();
    };
    window.addEventListener("smartimprove:currency-changed", onCur);
    return () => window.removeEventListener("smartimprove:currency-changed", onCur);
  }, [engagementId, load]);
  if (!data) return null;
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="roi-rec-inputs">
      <header className="px-5 py-3 border-b border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-[#111827]">Recommendation ROI Inputs</h3>
        <p className="text-[11px] text-[#6B7280] mt-0.5">
          Bottom-up benefit assumptions using client operational baselines and PrimeS4 Value Benchmarks.
          <span className="ml-1 text-[#9CA3AF]">Primary recommendations calculate one benefit component; strategic recommendations may carry several (one per operational area). These recommendations feed the business case above.</span>
        </p>
      </header>
      <div className="p-4 space-y-3">
        {data.recommendations.length === 0 ? (
          <p className="text-xs text-[#6B7280]">No accepted Primary Recommendations yet.</p>
        ) : null}
        {data.recommendations.map((c) => (
          <RecRoiCard
            key={c.recommendation_id}
            card={c}
            currency={currency}
            processOptions={data.process_options}
            valueDriverOptions={data.value_driver_options}
            strategicProjectOptions={data.strategic_project_options || []}
            onSave={async (patch) => {
              try {
                await ax.put(`/engagements/${engagementId}/roi-recommendation-inputs/${c.recommendation_id}`, patch);
                toast.success("Recommendation ROI input saved");
                await load();
              } catch (e) {
                const d = e?.response?.data?.detail;
                toast.error(typeof d === "string" ? d : "Save failed");
              }
            }}
          />
        ))}
      </div>
    </section>
  );
}

function RecRoiCard({ card, currency, processOptions, valueDriverOptions, strategicProjectOptions, onSave }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const st = ROI_STATUS[card.status] || ROI_STATUS.unmapped;
  const isMulti = !!card.is_strategic;
  const [form, setForm] = useState({});
  const startEdit = () => {
    setForm({
      scenario: card.scenario,
      process_applicability_id: card.process?.process_id || "",
      value_driver_metric_id: card.value_driver?.metric_id || "",
      applicable_warehouse_ids: [...card.applicable_warehouse_ids],
      process_applicability_override_pct: card.process?.is_override ? card.process.pct : "",
      value_driver_override: card.value_driver?.is_override ? card.value_driver.pct : "",
      annual_benefit_override_usd: card.benefit_is_override ? card.annual_benefit_override_usd : "",
      override_note: card.override_note || "",
    });
    setEdit(true);
    setOpen(true);
  };
  const toggleWh = (sid) => setForm((f) => ({
    ...f,
    applicable_warehouse_ids: f.applicable_warehouse_ids.includes(sid)
      ? f.applicable_warehouse_ids.filter((x) => x !== sid)
      : [...f.applicable_warehouse_ids, sid],
  }));
  const save = () => {
    const patch = {
      scenario: form.scenario,
      applicable_warehouse_ids: form.applicable_warehouse_ids,
    };
    if (form.process_applicability_id) patch.process_applicability_id = form.process_applicability_id;
    if (form.value_driver_metric_id) patch.value_driver_metric_id = form.value_driver_metric_id;
    if (form.process_applicability_override_pct !== "") patch.process_applicability_override_pct = Number(form.process_applicability_override_pct);
    if (form.value_driver_override !== "") patch.value_driver_override = Number(form.value_driver_override);
    if (form.annual_benefit_override_usd !== "") patch.annual_benefit_override_usd = Number(form.annual_benefit_override_usd);
    if (form.override_note) patch.override_note = form.override_note;
    onSave(patch).then(() => setEdit(false));
  };
  return (
    <article className="border border-[#E5E7EB] rounded-sm" data-testid={`roi-rec-card-${card.rec_code}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full text-left px-4 py-2.5 flex items-center gap-2 flex-wrap">
        {open ? <ChevronDown className="w-4 h-4 text-[#025979] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#025979] shrink-0" />}
        <span className="text-[10px] font-bold tracking-wider text-[#025979]">{card.rec_code}</span>
        {card.is_strategic && <span className="inline-flex px-1.5 py-0.5 text-[9px] font-semibold border border-[#C7B9F1] text-[#6D28D9] bg-[#F5F3FF] rounded-sm" data-testid={`roi-rec-strategic-badge-${card.rec_code}`}>Strategic</span>}
        <span className="text-sm font-semibold text-[#111827]">{card.rec_title}</span>
        <span className="text-[11px] text-[#6B7280]">{card.finding_count} finding{card.finding_count === 1 ? "" : "s"} · {card.applicable_warehouse_ids.length} warehouse{card.applicable_warehouse_ids.length === 1 ? "" : "s"} · {card.scenario}</span>
        <span className={`ml-auto inline-flex px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm ${st.cls}`}>{st.label}</span>
        {card.status === "partial" ? <span className="text-[10px] text-[#A16207] font-medium">{card.components_calculated} of {card.component_count} quantified</span> : null}
        <span className="text-sm font-bold text-[#047857] tabular-nums">{card.final_annual_benefit_usd != null ? `${fmtMoney(card.final_annual_benefit_usd, currency)}/yr` : "—"}</span>
      </button>
      {open ? (
        isMulti ? (
          <StrategicComponentsView
            card={card}
            currency={currency}
            processOptions={processOptions}
            valueDriverOptions={valueDriverOptions}
            strategicProjectOptions={strategicProjectOptions}
            onSave={onSave}
          />
        ) : (
        <div className="px-4 pb-4 border-t border-[#F1F5F9] pt-3 grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs">
          {/* Client Baseline */}
          <div>
            <h5 className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">Client Baseline</h5>
            <div className="text-[#6B7280] mb-1">Applicable Warehouses</div>
            <ul className="space-y-0.5 mb-2">
              {card.warehouse_baseline.map((w) => (
                <li key={w.site_id} className="flex justify-between gap-2">
                  <span className="text-[#374151]">{w.warehouse_name}<span className="text-[#9CA3AF]"> · {w.labor_cost_source}</span></span>
                  <span className="tabular-nums text-[#374151]">{w.annual_labor_baseline_usd != null ? fmtMoney(w.annual_labor_baseline_usd, currency) : <span className="text-[#B91C1C]">Baseline Required</span>}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between border-t border-dashed border-[#E5E7EB] pt-1">
              <span className="text-[#6B7280]">Economic Pool</span><span className="font-semibold text-[#374151] capitalize">{card.economic_pool || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#6B7280]">Applicable Labor Baseline</span>
              <span className="font-semibold tabular-nums">{card.applicable_labor_baseline_usd != null ? fmtMoney(card.applicable_labor_baseline_usd, currency) : <span className="text-[#B91C1C]">Baseline Required</span>}</span>
            </div>
          </div>
          {/* Benchmark Assumptions */}
          <div>
            <h5 className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">Benchmark Assumptions</h5>
            <div className="text-[#6B7280]">Process</div>
            <div className="text-[#374151] font-semibold">{card.process?.process_name || <span className="text-[#B91C1C]">Not selected</span>}</div>
            <div className="flex justify-between mb-1">
              <span className="text-[#6B7280]">Process Applicability</span>
              <span className="font-semibold">{card.process?.pct != null ? `${card.process.pct}%` : "—"} <span className={`text-[9px] ${card.process?.is_override ? "text-[#C2410C]" : "text-[#9CA3AF]"}`}>{card.process?.is_override ? "Override" : "PrimeS4"}</span></span>
            </div>
            <div className="text-[#6B7280] mt-1">Value Driver</div>
            <div className="text-[#374151] font-semibold">{card.value_driver?.metric_name || card.value_driver?.metric_id || <span className="text-[#B91C1C]">Not selected</span>}</div>
            <div className="flex justify-between">
              <span className="text-[#6B7280]">Improvement</span>
              <span className="font-semibold">{card.value_driver?.pct != null ? `${card.value_driver.pct}%` : "—"} <span className={`text-[9px] ${card.value_driver?.is_override ? "text-[#C2410C]" : "text-[#9CA3AF]"}`}>{card.value_driver?.is_override ? "Override" : "PrimeS4"}</span></span>
            </div>
            {card.mapping_basis ? <div className="text-[9px] text-[#9CA3AF] mt-1">Auto-mapped: {card.mapping_basis}</div> : null}
          </div>
          {/* Calculated Benefit */}
          <div>
            <h5 className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">Calculated Benefit</h5>
            <div className="flex justify-between"><span className="text-[#6B7280]">Process Economic Pool</span><span className="font-semibold tabular-nums">{card.process_economic_pool_usd != null ? fmtMoney(card.process_economic_pool_usd, currency) : "—"}</span></div>
            <div className="flex justify-between"><span className="text-[#6B7280]">Annual Gross Benefit</span><span className="font-semibold tabular-nums">{card.annual_gross_benefit_usd != null ? fmtMoney(card.annual_gross_benefit_usd, currency) : "—"}</span></div>
            {card.benefit_is_override ? (
              <div className="flex justify-between mt-1"><span className="text-[#C2410C]">Override Benefit</span><span className="font-bold tabular-nums text-[#C2410C]">{fmtMoney(card.annual_benefit_override_usd, currency)}</span></div>
            ) : null}
            <div className="flex justify-between border-t border-dashed border-[#E5E7EB] mt-1 pt-1"><span className="text-[#111827] font-semibold">Final Annual Benefit</span><span className="font-bold tabular-nums text-[#047857]">{card.final_annual_benefit_usd != null ? fmtMoney(card.final_annual_benefit_usd, currency) : "—"}</span></div>
            <div className="text-[9px] text-[#9CA3AF] mt-1">WS: {(card.linked_workstream_codes || []).join(", ") || "—"} · Findings: {(card.finding_codes || []).join(", ")}</div>
            {(card.notes || []).map((n, i) => <div key={i} className="text-[9px] text-[#B91C1C] mt-1">{n}</div>)}
            {!edit ? (
              <Button size="sm" variant="outline" onClick={startEdit} className="h-6 px-2 text-[11px] rounded-sm mt-2" data-testid={`roi-rec-edit-${card.rec_code}`}><Pencil className="w-3 h-3 mr-1" />Adjust Inputs</Button>
            ) : null}
          </div>
          {edit ? (
            <div className="lg:col-span-3 border-t border-[#E5E7EB] pt-3 space-y-2" data-testid={`roi-rec-editor-${card.rec_code}`}>
              <div className="flex flex-wrap gap-3">
                <label className="text-[11px] text-[#374151]">Scenario
                  <select value={form.scenario} onChange={(e) => setForm((f) => ({ ...f, scenario: e.target.value }))} className="ml-1 border border-[#E5E7EB] rounded-sm text-[11px] px-1 py-0.5" data-testid={`roi-rec-scenario-${card.rec_code}`}>
                    <option>Conservative</option><option>Expected</option><option>Optimistic</option>
                  </select>
                </label>
                <label className="text-[11px] text-[#374151]">Process
                  <select value={form.process_applicability_id} onChange={(e) => setForm((f) => ({ ...f, process_applicability_id: e.target.value }))} className="ml-1 border border-[#E5E7EB] rounded-sm text-[11px] px-1 py-0.5">
                    <option value="">— select —</option>
                    {processOptions.map((p) => <option key={p.process_id} value={p.process_id}>{p.process_name}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-[#374151]">Value Driver
                  <select value={form.value_driver_metric_id} onChange={(e) => setForm((f) => ({ ...f, value_driver_metric_id: e.target.value }))} className="ml-1 border border-[#E5E7EB] rounded-sm text-[11px] px-1 py-0.5">
                    <option value="">— select —</option>
                    {valueDriverOptions.map((m) => <option key={m.metric_id} value={m.metric_id}>{m.metric_name}</option>)}
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                <span className="text-[11px] text-[#6B7280]">Applicable warehouses:</span>
                {card.all_warehouses.map((w) => (
                  <label key={w.site_id} className="text-[11px] text-[#374151] flex items-center gap-1">
                    <input type="checkbox" checked={form.applicable_warehouse_ids.includes(w.site_id)} onChange={() => toggleWh(w.site_id)} className="accent-[#025979]" />
                    {w.warehouse_name}
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <label className="text-[11px] text-[#374151]">PA % override<Input value={form.process_applicability_override_pct} onChange={(e) => setForm((f) => ({ ...f, process_applicability_override_pct: e.target.value }))} className="ml-1 h-6 w-20 inline-block text-[11px]" placeholder="bench" /></label>
                <label className="text-[11px] text-[#374151]">VD % override<Input value={form.value_driver_override} onChange={(e) => setForm((f) => ({ ...f, value_driver_override: e.target.value }))} className="ml-1 h-6 w-20 inline-block text-[11px]" placeholder="bench" /></label>
                <label className="text-[11px] text-[#374151]">Annual benefit override (USD)<Input value={form.annual_benefit_override_usd} onChange={(e) => setForm((f) => ({ ...f, annual_benefit_override_usd: e.target.value }))} className="ml-1 h-6 w-28 inline-block text-[11px]" placeholder="calc" /></label>
              </div>
              <Input value={form.override_note} onChange={(e) => setForm((f) => ({ ...f, override_note: e.target.value }))} className="h-7 text-[11px]" placeholder="Override rationale (required when overriding)…" data-testid={`roi-rec-note-${card.rec_code}`} />
              <div className="flex gap-2">
                <Button size="sm" onClick={save} className="h-7 px-3 text-[11px] bg-[#025979] hover:bg-[#03738C] text-white rounded-sm" data-testid={`roi-rec-save-${card.rec_code}`}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEdit(false)} className="h-7 px-3 text-[11px] rounded-sm">Cancel</Button>
              </div>
            </div>
          ) : null}
        </div>
        )
      ) : null}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Strategic multi-component view — one block per benefit component.
// ---------------------------------------------------------------------------
function StrategicComponentsView({ card, currency, processOptions, valueDriverOptions, strategicProjectOptions, onSave }) {
  const [editingId, setEditingId] = useState(null);
  const sp = card.strategic_project || {};
  const [projSel, setProjSel] = useState(sp.project_id || "");
  const [ovEdit, setOvEdit] = useState(false);
  const [ovVal, setOvVal] = useState(sp.benefit_is_override ? sp.annual_benefit_override_usd : "");
  const [ovNote, setOvNote] = useState(sp.override_note || "");
  const scen = card.scenario;
  return (
    <div className="px-4 pb-4 border-t border-[#F1F5F9] pt-3 space-y-4 text-xs" data-testid={`roi-rec-components-${card.rec_code}`}>
      {/* --- Strategic Project Benefit (drives Final Annual Benefit) --- */}
      <div className="rounded-sm border border-[#C7B9F1] bg-[#F5F3FF] p-3 space-y-2" data-testid={`roi-rec-strategic-panel-${card.rec_code}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h5 className="text-[10px] font-semibold uppercase tracking-wider text-[#6D28D9]">Strategic Project Benefit</h5>
          <label className="text-[11px] text-[#374151]">Scenario
            <select
              value={scen}
              onChange={(e) => onSave({ scenario: e.target.value })}
              className="ml-1 border border-[#C7B9F1] rounded-sm text-[11px] px-1 py-0.5 bg-white"
              data-testid={`roi-rec-scenario-${card.rec_code}`}
            >
              <option>Conservative</option><option>Expected</option><option>Optimistic</option>
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-[#374151]">Strategic Project Benchmark</span>
          <select
            value={projSel}
            onChange={(e) => { setProjSel(e.target.value); onSave({ strategic_project_id: e.target.value }); }}
            className="border border-[#C7B9F1] rounded-sm text-[11px] px-1 py-1 bg-white min-w-[220px]"
            data-testid={`roi-rec-strategic-select-${card.rec_code}`}
          >
            <option value="">— Select strategic project —</option>
            {(strategicProjectOptions || []).map((p) => (
              <option key={p.project_id} value={p.project_id} disabled={!p.active}>
                {p.project_name}{p.active ? "" : " (inactive)"}
              </option>
            ))}
          </select>
        </div>
        {sp.project_id ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
            <div>
              <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-0.5">Baseline ({sp.baseline_source || "direct labor"})</div>
              <div className="tabular-nums font-semibold text-[#111827]" data-testid={`roi-rec-strategic-baseline-${card.rec_code}`}>
                {sp.applicable_labor_baseline_usd != null ? fmtMoney(sp.applicable_labor_baseline_usd, currency) : <span className="text-[#B91C1C]">Baseline Required</span>}
              </div>
              <div className="text-[9px] text-[#9CA3AF] mt-0.5">{(sp.warehouse_baseline || []).map((w) => w.warehouse_name).join(", ")}</div>
            </div>
            <div>
              <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-0.5">{scen} Improvement %</div>
              <div className="tabular-nums font-semibold text-[#111827]" data-testid={`roi-rec-strategic-pct-${card.rec_code}`}>
                {sp.pct != null ? `${sp.pct}%` : <span className="text-[#B91C1C]">Not set</span>}
              </div>
              <div className="text-[9px] text-[#9CA3AF] mt-0.5">Range {sp.low_pct ?? "—"}% / {sp.expected_pct ?? "—"}% / {sp.high_pct ?? "—"}%</div>
            </div>
            <div>
              <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-0.5">Strategic Benefit</div>
              <div className="tabular-nums font-bold text-[#047857]" data-testid={`roi-rec-strategic-benefit-${card.rec_code}`}>
                {sp.annual_gross_benefit_usd != null ? fmtMoney(sp.annual_gross_benefit_usd, currency) : "—"}
              </div>
              {sp.benefit_is_override ? <div className="text-[9px] text-[#C2410C] mt-0.5">Override: {fmtMoney(sp.annual_benefit_override_usd, currency)}</div> : null}
            </div>
          </div>
        ) : null}
        {sp.project_id ? (
          <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-[#DDD6FE]">
            {!ovEdit ? (
              <Button size="sm" variant="outline" onClick={() => { setOvEdit(true); setOvVal(sp.benefit_is_override ? sp.annual_benefit_override_usd : ""); setOvNote(sp.override_note || ""); }} className="h-6 px-2 text-[11px] rounded-sm mt-1" data-testid={`roi-rec-strategic-override-btn-${card.rec_code}`}>
                <Pencil className="w-3 h-3 mr-1" />Consultant Override
              </Button>
            ) : (
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <label className="text-[11px] text-[#374151]">Annual benefit override (USD)
                  <Input value={ovVal} onChange={(e) => setOvVal(e.target.value)} className="ml-1 h-6 w-28 inline-block text-[11px]" placeholder="calc" data-testid={`roi-rec-strategic-override-input-${card.rec_code}`} />
                </label>
                <Input value={ovNote} onChange={(e) => setOvNote(e.target.value)} className="h-6 w-56 text-[11px]" placeholder="Override rationale…" data-testid={`roi-rec-strategic-override-note-${card.rec_code}`} />
                <Button size="sm" onClick={() => { const patch = { annual_benefit_override_usd: ovVal === "" ? null : Number(ovVal) }; if (ovNote) patch.override_note = ovNote; onSave(patch).then(() => setOvEdit(false)); }} className="h-6 px-3 text-[11px] bg-[#025979] hover:bg-[#03738C] text-white rounded-sm" data-testid={`roi-rec-strategic-override-save-${card.rec_code}`}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setOvEdit(false)} className="h-6 px-3 text-[11px] rounded-sm">Cancel</Button>
              </div>
            )}
          </div>
        ) : null}
        {(sp.notes || []).map((n, i) => <div key={i} className="text-[9px] text-[#B91C1C]">{n}</div>)}
        <div className="flex items-baseline justify-between border-t border-[#DDD6FE] pt-2">
          <span className="text-[12px] font-bold uppercase tracking-wider text-[#111827]">Final Annual Benefit</span>
          <span className="text-[16px] font-bold tabular-nums text-[#047857]" data-testid={`roi-rec-final-${card.rec_code}`}>
            {card.final_annual_benefit_usd != null ? `${fmtMoney(card.final_annual_benefit_usd, currency)}/year` : "—"}
          </span>
        </div>
      </div>

      {/* --- Supporting bottom-up analysis (NOT summed into Final Benefit) --- */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h5 className="text-[10px] font-semibold uppercase tracking-wider text-[#025979]">Supporting Opportunity Areas</h5>
        <span className="text-[9px] text-[#9CA3AF]" data-testid={`roi-rec-supporting-note-${card.rec_code}`}>Diagnostic evidence only · not the strategic financial calculation</span>
      </div>
      {card.benefit_components.map((comp, idx) => (
        <ComponentBlock
          key={comp.component_id}
          card={card}
          comp={comp}
          idx={idx}
          currency={currency}
          processOptions={processOptions}
          valueDriverOptions={valueDriverOptions}
          editing={editingId === comp.component_id}
          onEdit={() => setEditingId(comp.component_id)}
          onCancel={() => setEditingId(null)}
          onSave={async (ov) => {
            await onSave({ component_overrides: { [comp.component_id]: ov } });
            setEditingId(null);
          }}
        />
      ))}
    </div>
  );
}

function ComponentBlock({ card, comp, idx, currency, processOptions, valueDriverOptions, editing, onEdit, onCancel, onSave }) {
  const isMapped = comp.status === "calculated" || comp.status === "override";
  const cst = ROI_STATUS[comp.status] || ROI_STATUS.unmapped;
  const areaLabel = (comp.process?.process_name || (comp.operational_area || "").replace(/_/g, " ")).toUpperCase();
  const [form, setForm] = useState({});
  const start = () => {
    setForm({
      process_applicability_id: comp.process?.process_id || "",
      value_driver_metric_id: comp.value_driver?.metric_id || "",
      applicable_warehouse_ids: [...comp.applicable_warehouse_ids],
      process_applicability_override_pct: comp.process?.is_override ? comp.process.pct : "",
      value_driver_override: comp.value_driver?.is_override ? comp.value_driver.pct : "",
      annual_benefit_override_usd: comp.benefit_is_override ? comp.annual_benefit_override_usd : "",
      override_note: comp.override_note || "",
    });
    onEdit();
  };
  const toggleWh = (sid) => setForm((f) => ({
    ...f,
    applicable_warehouse_ids: f.applicable_warehouse_ids.includes(sid)
      ? f.applicable_warehouse_ids.filter((x) => x !== sid)
      : [...f.applicable_warehouse_ids, sid],
  }));
  const save = () => {
    const ov = { applicable_warehouse_ids: form.applicable_warehouse_ids };
    if (form.process_applicability_id) ov.process_applicability_id = form.process_applicability_id;
    if (form.value_driver_metric_id) ov.value_driver_metric_id = form.value_driver_metric_id;
    if (form.process_applicability_override_pct !== "") ov.process_applicability_override_pct = Number(form.process_applicability_override_pct);
    if (form.value_driver_override !== "") ov.value_driver_override = Number(form.value_driver_override);
    if (form.annual_benefit_override_usd !== "") ov.annual_benefit_override_usd = Number(form.annual_benefit_override_usd);
    if (form.override_note) ov.override_note = form.override_note;
    onSave(ov);
  };
  return (
    <div className="border border-[#E5E7EB] rounded-sm p-3" data-testid={`roi-comp-${card.rec_code}-${comp.component_id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-[11px] font-bold text-[#025979]">{idx + 1}. {areaLabel}</span>
        <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm ${cst.cls}`}>{isMapped ? "Opportunity Area" : "Mapping Required"}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0.5">
        <Line label="Findings" value={(comp.finding_codes || []).join(", ") || "—"} />
        <Line label="Applicable Warehouses" value={String(comp.applicable_warehouse_ids.length)} />
        {isMapped ? (
          <>
            <Line label="Process Applicability" value={`${comp.process?.pct}% · ${comp.process?.is_override ? "Override" : "PrimeS4"}`} />
            <Line label="Value Driver" value={comp.value_driver?.metric_name || comp.value_driver?.metric_id || "—"} />
            <Line label="Improvement" value={`${comp.value_driver?.pct}% · ${comp.value_driver?.is_override ? "Override" : "PrimeS4"}`} />
          </>
        ) : (
          <Line label="Operational Area" value={(comp.operational_area || "—").replace(/_/g, " ")} />
        )}
      </div>
      {(comp.notes || []).map((n, i) => <div key={i} className="text-[9px] text-[#B91C1C] mt-1">{n}</div>)}
      {!editing ? (
        <Button size="sm" variant="outline" onClick={start} className="h-6 px-2 text-[11px] rounded-sm mt-2" data-testid={`roi-comp-edit-${card.rec_code}-${comp.component_id}`}>
          <Pencil className="w-3 h-3 mr-1" />Adjust Component
        </Button>
      ) : (
        <div className="border-t border-[#E5E7EB] mt-2 pt-2 space-y-2" data-testid={`roi-comp-editor-${card.rec_code}-${comp.component_id}`}>
          <div className="flex flex-wrap gap-3">
            <label className="text-[11px] text-[#374151]">Process
              <select value={form.process_applicability_id} onChange={(e) => setForm((f) => ({ ...f, process_applicability_id: e.target.value }))} className="ml-1 border border-[#E5E7EB] rounded-sm text-[11px] px-1 py-0.5">
                <option value="">— select —</option>
                {processOptions.map((p) => <option key={p.process_id} value={p.process_id}>{p.process_name}</option>)}
              </select>
            </label>
            <label className="text-[11px] text-[#374151]">Value Driver
              <select value={form.value_driver_metric_id} onChange={(e) => setForm((f) => ({ ...f, value_driver_metric_id: e.target.value }))} className="ml-1 border border-[#E5E7EB] rounded-sm text-[11px] px-1 py-0.5">
                <option value="">— select —</option>
                {valueDriverOptions.map((m) => <option key={m.metric_id} value={m.metric_id}>{m.metric_name}</option>)}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-[11px] text-[#6B7280]">Applicable warehouses:</span>
            {card.all_warehouses.map((w) => (
              <label key={w.site_id} className="text-[11px] text-[#374151] flex items-center gap-1">
                <input type="checkbox" checked={form.applicable_warehouse_ids.includes(w.site_id)} onChange={() => toggleWh(w.site_id)} className="accent-[#025979]" />
                {w.warehouse_name}
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="text-[11px] text-[#374151]">PA % override<Input value={form.process_applicability_override_pct} onChange={(e) => setForm((f) => ({ ...f, process_applicability_override_pct: e.target.value }))} className="ml-1 h-6 w-20 inline-block text-[11px]" placeholder="bench" /></label>
            <label className="text-[11px] text-[#374151]">VD % override<Input value={form.value_driver_override} onChange={(e) => setForm((f) => ({ ...f, value_driver_override: e.target.value }))} className="ml-1 h-6 w-20 inline-block text-[11px]" placeholder="bench" /></label>
            <label className="text-[11px] text-[#374151]">Annual benefit override (USD)<Input value={form.annual_benefit_override_usd} onChange={(e) => setForm((f) => ({ ...f, annual_benefit_override_usd: e.target.value }))} className="ml-1 h-6 w-28 inline-block text-[11px]" placeholder="calc" /></label>
          </div>
          <Input value={form.override_note} onChange={(e) => setForm((f) => ({ ...f, override_note: e.target.value }))} className="h-7 text-[11px]" placeholder="Override rationale…" data-testid={`roi-comp-note-${card.rec_code}-${comp.component_id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} className="h-7 px-3 text-[11px] bg-[#025979] hover:bg-[#03738C] text-white rounded-sm" data-testid={`roi-comp-save-${card.rec_code}-${comp.component_id}`}>Save Component</Button>
            <Button size="sm" variant="outline" onClick={onCancel} className="h-7 px-3 text-[11px] rounded-sm">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Line({ label, value, danger = false }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[#6B7280]">{label}</span>
      <span className={`font-semibold tabular-nums text-right ${danger ? "text-[#B91C1C]" : "text-[#374151]"}`}>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Override Dialog
// ---------------------------------------------------------------------------
function OverrideDialog({ engagementId, overrides, dashboard, onClose, onSaved, onReset }) {
  const [customAnnual, setCustomAnnual] = useState(
    overrides.custom_annual_savings_usd ?? ""
  );
  const [year1Pct, setYear1Pct] = useState(
    overrides.year1_realization_pct ?? dashboard.assumptions_snapshot?.year1_realization_pct ?? 60
  );
  const [confidencePct, setConfidencePct] = useState(
    overrides.confidence_multiplier_pct ?? dashboard.assumptions_snapshot?.confidence_pct ?? 80
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        year1_realization_pct: Number(year1Pct),
        confidence_multiplier_pct: Number(confidencePct),
      };
      if (customAnnual !== "" && customAnnual !== null) {
        payload.custom_annual_savings_usd = Number(customAnnual);
      }
      await ax.put(`/engagements/${engagementId}/roi-overrides`, payload);
      toast.success("ROI overrides saved");
      await onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save overrides");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="roi-override-dialog">
        <DialogHeader>
          <DialogTitle>Edit ROI Inputs / Override</DialogTitle>
          <DialogDescription>
            Pin an annual savings number or tune realization assumptions. ROI
            recalculates immediately on save.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Custom Annual Savings (USD)" hint="Leave blank to use the calculated value.">
            <Input
              type="number"
              min="0"
              value={customAnnual}
              onChange={(e) => setCustomAnnual(e.target.value)}
              placeholder={`Calculated: ${Math.round(dashboard.annual_savings_usd).toLocaleString()}`}
              className="rounded-sm text-sm"
              data-testid="roi-override-savings"
            />
          </Field>
          <Field label="Year-1 Realization %" hint="Ramp-up benefit captured in Year 1.">
            <Input
              type="number"
              min="0"
              max="100"
              step="1"
              value={year1Pct}
              onChange={(e) => setYear1Pct(e.target.value)}
              className="rounded-sm text-sm"
              data-testid="roi-override-year1"
            />
          </Field>
          <Field label="Confidence Multiplier %" hint="Haircut on the raw savings estimate (e.g. 80% = expected scenario).">
            <Input
              type="number"
              min="0"
              max="100"
              step="1"
              value={confidencePct}
              onChange={(e) => setConfidencePct(e.target.value)}
              className="rounded-sm text-sm"
              data-testid="roi-override-confidence"
            />
          </Field>
        </div>
        <DialogFooter className="flex items-center justify-between flex-wrap gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onReset}
            className="rounded-sm text-xs text-[#B91C1C]"
            data-testid="roi-override-reset"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> Reset to defaults
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={onClose}
              className="rounded-sm text-xs"
              data-testid="roi-override-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
              data-testid="roi-override-save"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1">
        {label}
      </label>
      {children}
      {hint ? <p className="text-[10px] text-[#9CA3AF] mt-1">{hint}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------
function EmptyState({ title, description }) {
  return (
    <div
      className="bg-white border border-dashed border-[#E5E7EB] rounded-sm py-16 px-6 text-center max-w-3xl mx-auto"
      data-testid="roi-empty-state"
    >
      <TrendingUp className="w-8 h-8 text-[#94A3B8] mx-auto mb-3" />
      <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
      <p className="text-xs text-[#6B7280] mt-1">{description}</p>
    </div>
  );
}
